/**
 * The nest api (docs/NESTS.md), typed and parsed defensively. Every call but the two
 * public ones sends this device's id in `X-Device-Id` (never in a URL), and every
 * response is untrusted: names are kept as plain strings and only ever rendered as text.
 *
 * Reads throw a {@link NestError}; nothing here is cached (the api says `no-store`, and
 * the service worker passes `/v1/nests` straight through, src/pwa/sw-core.ts).
 */
import { isLevel, type Level } from '@arrow/puzzle-schema';

import { API_URL } from '../env.ts';
import { getDeviceId } from '../platform/identity.ts';

export type NestRole = 'founder' | 'member';
/** A member's day at one level, as the boards show it (docs/NESTS.md, "Fair play"). */
export type BoardState = 'none' | 'playing' | 'solved' | 'help' | 'late' | 'unverified';
const STATES: readonly BoardState[] = ['none', 'playing', 'solved', 'help', 'late', 'unverified'];

export interface NestMember {
	memberId: string;
	displayName: string;
	role: NestRole;
	/** `YYYY-MM-DD`: their solves count from this daily on. */
	sinceDate: string;
}

export interface NestView {
	id: string;
	name: string;
	code: string;
	/** The invite link (`/n/:code` on the site). */
	url: string;
	createdDate: string;
	me: NestMember & { muted: boolean };
	/** Oldest member first. */
	members: NestMember[];
}

export interface NestSummary {
	id: string;
	name: string;
	memberCount: number;
	role: NestRole;
	muted: boolean;
}

export interface InviteView {
	code: string;
	name: string;
	invitedBy: string;
	/** First names, oldest member first. */
	members: string[];
	memberCount: number;
	full: boolean;
	/** Whether this device is already in it. */
	joined: boolean | null;
}

export interface FeathersInfo {
	text: string;
	numbers: string;
}

export interface Run {
	days: number;
	shieldUsed: boolean;
	today: { played: number; members: number };
}

export interface TodayEntry {
	memberId: string;
	state: BoardState;
	durationMs: number | null;
	rank: number | null;
	/** Answers revealed on this solve (Nest boards v2): ≥ 1 on a `help` solve, 0 otherwise. Null from an api that doesn't send it yet. */
	reveals?: number | null;
}

export interface TodayBoard {
	date: string;
	number: number;
	me: string;
	run: Run;
	feathers: FeathersInfo | null;
	levels: { level: Level; entries: TodayEntry[] }[];
}

/** One day on the week board's strip: nothing, a solve, or a solve that used a reveal. */
export interface WeekDay {
	date: string;
	state: 'none' | 'played' | 'reveals';
}

export interface FeatherEntry {
	memberId: string;
	feathers: number;
	solves: number;
	rank: number;
	/** Nest boards v2, each null from an api that doesn't send it yet: reveals across the counted solves in range. */
	reveals?: number | null;
	/** Distinct puzzle dates in range with a counted solve. */
	days?: number | null;
	/** The week board's Mon–Sun strip (7 days, `from` to `to`); null on All time. ("New" reads the member's `sinceDate`.) */
	week?: WeekDay[] | null;
}

export interface FeatherBoard {
	from: string;
	to: string;
	final: boolean;
	me: string;
	feathers: FeathersInfo | null;
	entries: FeatherEntry[];
}

export type ActivityKind = 'join' | 'solve' | 'nudge';

export interface ActivityItem {
	id: string;
	kind: ActivityKind;
	at: string;
	memberId: string;
	targetMemberId: string | null;
	number: number | null;
	phrase: string | null;
	levels: { level: Level; state: BoardState }[];
	wellDone: { count: number; mine: boolean };
}

/** The set phrases a nudge can carry (the api's keys). The nest tab sends `your_go`. */
export type NudgePhrase = 'your_go' | 'easy_quick' | 'good_luck';

/**
 * What went wrong, in terms a screen can act on. `network`: never reached the api, or a
 * 5xx / 429. `not-found` is also what an api without NESTS_ENABLED answers.
 */
export type NestErrorKind = 'network' | 'not-found' | 'conflict' | 'forbidden' | 'invalid' | 'bad-request';

export class NestError extends Error {
	constructor(
		readonly kind: NestErrorKind,
		readonly status: number,
		message: string,
	) {
		super(message);
	}
}

/** Six characters of the api's alphabet (case-insensitive); what `/n/:code` and the code field take. */
export const NEST_CODE = /^[A-Za-z0-9]{6}$/;

export const normaliseCode = (raw: string): string => raw.replace(/[\s-]+/g, '').toUpperCase();

// ---------------------------------------------------------------------------- transport

export interface NestTransport {
	fetch: typeof fetch;
	deviceId: () => string;
	apiUrl: string;
}

const defaultTransport = (): NestTransport => ({ fetch: (...a) => fetch(...a), deviceId: () => getDeviceId(), apiUrl: API_URL });
let transport: NestTransport | null = null;
const t = (): NestTransport => (transport ??= defaultTransport());

/** Tests: swap the transport (null: back to the real one). */
export function setNestTransport(next: NestTransport | null): void {
	transport = next;
}

async function call(
	method: 'GET' | 'POST',
	path: string,
	opts: { body?: unknown; device?: boolean; signal?: AbortSignal | undefined } = {},
): Promise<unknown> {
	const tr = t();
	const headers: Record<string, string> = { Accept: 'application/json' };
	if (opts.device !== false) headers['X-Device-Id'] = tr.deviceId();
	if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
	let res: Response;
	try {
		res = await tr.fetch(`${tr.apiUrl}/v1/nests${path}`, {
			method,
			headers,
			cache: 'no-store',
			...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
			...(opts.signal ? { signal: opts.signal } : {}),
		});
	} catch (err) {
		if (err instanceof DOMException && err.name === 'AbortError') throw err;
		throw new NestError('network', 0, 'Could not reach Sparrow.');
	}
	if (res.status === 204) return null;
	const body = (await res.json().catch(() => null)) as unknown;
	if (!res.ok) throw new NestError(kindOf(res.status), res.status, messageOf(body));
	return body;
}

function kindOf(status: number): NestErrorKind {
	if (status === 404) return 'not-found';
	if (status === 409) return 'conflict';
	if (status === 403) return 'forbidden';
	if (status === 400) return 'bad-request';
	return 'network';
}

function messageOf(body: unknown): string {
	const m = field(body, 'message');
	if (typeof m === 'string') return m.slice(0, 200);
	if (Array.isArray(m) && typeof m[0] === 'string') return m[0].slice(0, 200);
	return '';
}

// ----------------------------------------------------------------------------- parsing

const field = (obj: unknown, key: string): unknown =>
	typeof obj === 'object' && obj !== null ? (obj as Record<string, unknown>)[key] : undefined;
const str = (v: unknown): v is string => typeof v === 'string';
const int = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const bad = (what: string) => new NestError('invalid', 200, `The ${what} answer was malformed.`);
const role = (v: unknown): NestRole => (v === 'founder' ? 'founder' : 'member');
const state = (v: unknown): BoardState => (STATES.includes(v as BoardState) ? (v as BoardState) : 'none');

function toMember(raw: unknown): NestMember | null {
	const memberId = field(raw, 'memberId');
	const displayName = field(raw, 'displayName');
	const sinceDate = field(raw, 'sinceDate');
	if (!str(memberId) || !str(displayName) || !str(sinceDate)) return null;
	return { memberId, displayName: displayName.slice(0, 80), role: role(field(raw, 'role')), sinceDate };
}

export function toNestView(raw: unknown): NestView {
	const id = field(raw, 'id');
	const name = field(raw, 'name');
	const code = field(raw, 'code');
	const url = field(raw, 'url');
	const me = toMember(field(raw, 'me'));
	const members = field(raw, 'members');
	if (!str(id) || !str(name) || !str(code) || !me || !Array.isArray(members)) throw bad('nest');
	const createdDate = field(raw, 'createdDate');
	return {
		id,
		name: name.slice(0, 80),
		code,
		url: str(url) ? url : '',
		createdDate: str(createdDate) && DATE.test(createdDate) ? createdDate : me.sinceDate,
		me: { ...me, muted: field(field(raw, 'me'), 'muted') === true },
		members: members.map(toMember).filter((m): m is NestMember => m !== null),
	};
}

function toFeathers(raw: unknown): FeathersInfo | null {
	const text = field(raw, 'text');
	const numbers = field(raw, 'numbers');
	return str(text) && str(numbers) ? { text, numbers } : null;
}

export function toTodayBoard(raw: unknown): TodayBoard {
	const date = field(raw, 'date');
	const number = field(raw, 'number');
	const me = field(raw, 'me');
	const levels = field(raw, 'levels');
	if (!str(date) || !int(number) || !str(me) || !Array.isArray(levels)) throw bad('board');
	const run = field(raw, 'run');
	const today = field(run, 'today');
	const n = (v: unknown) => (int(v) && v >= 0 ? v : 0);
	return {
		date,
		number,
		me,
		run: { days: n(field(run, 'days')), shieldUsed: field(run, 'shieldUsed') === true, today: { played: n(field(today, 'played')), members: n(field(today, 'members')) } },
		feathers: toFeathers(field(raw, 'feathers')),
		levels: levels.flatMap((l) => {
			const level = field(l, 'level');
			const entries = field(l, 'entries');
			if (!isLevel(level) || !Array.isArray(entries)) return [];
			return [
				{
					level,
					entries: entries.flatMap((e): TodayEntry[] => {
						const memberId = field(e, 'memberId');
						if (!str(memberId)) return [];
						const ms = field(e, 'durationMs');
						const rank = field(e, 'rank');
						const reveals = field(e, 'reveals');
						return [
							{
								memberId,
								state: state(field(e, 'state')),
								durationMs: typeof ms === 'number' && ms >= 0 ? ms : null,
								rank: int(rank) && rank > 0 ? rank : null,
								reveals: int(reveals) && reveals >= 0 ? reveals : null,
							},
						];
					}),
				},
			];
		}),
	};
}

export function toFeatherBoard(raw: unknown): FeatherBoard {
	const entries = field(raw, 'entries');
	const me = field(raw, 'me');
	if (!Array.isArray(entries) || !str(me)) throw bad('board');
	const from = field(raw, 'from');
	const to = field(raw, 'to');
	return {
		from: str(from) ? from : '',
		to: str(to) ? to : '',
		final: field(raw, 'final') === true,
		me,
		feathers: toFeathers(field(raw, 'feathers')),
		entries: entries.flatMap((e): FeatherEntry[] => {
			const memberId = field(e, 'memberId');
			const feathers = field(e, 'feathers');
			const rank = field(e, 'rank');
			const solves = field(e, 'solves');
			if (!str(memberId) || !int(feathers) || !int(rank)) return [];
			const reveals = field(e, 'reveals');
			const days = field(e, 'days');
			const week = field(e, 'week');
			return [
				{
					memberId,
					feathers: Math.max(0, feathers),
					rank,
					solves: int(solves) ? solves : 0,
					reveals: int(reveals) && reveals >= 0 ? reveals : null,
					days: int(days) && days >= 0 ? days : null,
					week: Array.isArray(week) ? week.flatMap(toWeekDay) : null,
				},
			];
		}),
	};
}

function toWeekDay(raw: unknown): WeekDay[] {
	const date = field(raw, 'date');
	const st = field(raw, 'state');
	if (!str(date) || !DATE.test(date)) return [];
	return [{ date, state: st === 'played' || st === 'reveals' ? st : 'none' }];
}

export function toActivity(raw: unknown): ActivityItem | null {
	const id = field(raw, 'id');
	const kind = field(raw, 'kind');
	const memberId = field(raw, 'memberId');
	if (!str(id) || !str(memberId) || (kind !== 'join' && kind !== 'solve' && kind !== 'nudge')) return null;
	const at = field(raw, 'at');
	const target = field(raw, 'targetMemberId');
	const number = field(raw, 'number');
	const phrase = field(raw, 'phrase');
	const levels = field(raw, 'levels');
	const wd = field(raw, 'wellDone');
	const count = field(wd, 'count');
	return {
		id,
		kind,
		at: str(at) ? at : '',
		memberId,
		targetMemberId: str(target) ? target : null,
		number: int(number) ? number : null,
		phrase: str(phrase) ? phrase : null,
		levels: Array.isArray(levels)
			? levels.flatMap((l) => {
					const level = field(l, 'level');
					return isLevel(level) ? [{ level, state: state(field(l, 'state')) }] : [];
				})
			: [],
		wellDone: { count: int(count) && count >= 0 ? count : 0, mine: field(wd, 'mine') === true },
	};
}

export function toInvite(raw: unknown): InviteView {
	const code = field(raw, 'code');
	const name = field(raw, 'name');
	const members = field(raw, 'members');
	if (!str(code) || !str(name) || !Array.isArray(members)) throw bad('invite');
	const invitedBy = field(raw, 'invitedBy');
	const count = field(raw, 'memberCount');
	const joined = field(raw, 'joined');
	const names = members.filter(str).map((m) => m.slice(0, 40));
	return {
		code,
		name: name.slice(0, 80),
		invitedBy: str(invitedBy) ? invitedBy.slice(0, 40) : '',
		members: names,
		memberCount: int(count) ? count : names.length,
		full: field(raw, 'full') === true,
		joined: typeof joined === 'boolean' ? joined : null,
	};
}

// ------------------------------------------------------------------------------- calls

const q = (date: string) => `?date=${encodeURIComponent(date)}`;
const seg = (id: string) => `/${encodeURIComponent(id)}`;

/** `GET /v1/nests/feathers` (public): the "Feathers ⓘ" words. */
export async function fetchFeathers(signal?: AbortSignal): Promise<FeathersInfo> {
	const info = toFeathers(await call('GET', '/feathers', { device: false, signal }));
	if (!info) throw bad('feathers');
	return info;
}

export async function fetchMyNests(signal?: AbortSignal): Promise<NestSummary[]> {
	const nests = field(await call('GET', '', { signal }), 'nests');
	if (!Array.isArray(nests)) throw bad('nests');
	return nests.flatMap((n): NestSummary[] => {
		const id = field(n, 'id');
		const name = field(n, 'name');
		const count = field(n, 'memberCount');
		if (!str(id) || !str(name)) return [];
		return [{ id, name: name.slice(0, 80), memberCount: int(count) ? count : 1, role: role(field(n, 'role')), muted: field(n, 'muted') === true }];
	});
}

export async function fetchNest(id: string, signal?: AbortSignal): Promise<NestView> {
	return toNestView(await call('GET', seg(id), { signal }));
}

export async function createNest(input: { name: string; displayName: string; date: string }): Promise<NestView> {
	return toNestView(await call('POST', '', { body: input }));
}

/** The public landing for a code; with `by`, who sent the link. Sends the device id so `joined` is known. */
export async function fetchInvite(code: string, by: string | null, signal?: AbortSignal): Promise<InviteView> {
	const c = normaliseCode(code);
	if (!NEST_CODE.test(c)) throw new NestError('not-found', 404, 'That code isn’t right.');
	const query = by && /^[0-9a-f-]{36}$/i.test(by) ? `?by=${encodeURIComponent(by)}` : '';
	return toInvite(await call('GET', `/invite/${encodeURIComponent(c)}${query}`, { signal }));
}

export async function joinNest(input: { code: string; displayName: string; date: string }): Promise<NestView> {
	return toNestView(await call('POST', '/join', { body: { ...input, code: normaliseCode(input.code) } }));
}

export async function updateMe(id: string, patch: { displayName?: string; muted?: boolean }): Promise<NestView> {
	return toNestView(await call('POST', `${seg(id)}/me`, { body: patch }));
}

export async function leaveNest(id: string): Promise<void> {
	await call('POST', `${seg(id)}/leave`);
}

export async function fetchToday(id: string, date: string, signal?: AbortSignal): Promise<TodayBoard> {
	return toTodayBoard(await call('GET', `${seg(id)}/today${q(date)}`, { signal }));
}

export async function fetchWeek(id: string, date: string, signal?: AbortSignal): Promise<FeatherBoard> {
	return toFeatherBoard(await call('GET', `${seg(id)}/week${q(date)}`, { signal }));
}

export async function fetchAllTime(id: string, date: string, signal?: AbortSignal): Promise<FeatherBoard> {
	return toFeatherBoard(await call('GET', `${seg(id)}/all-time${q(date)}`, { signal }));
}

export async function fetchActivity(id: string, signal?: AbortSignal): Promise<ActivityItem[]> {
	const items = field(await call('GET', `${seg(id)}/activity?limit=30`, { signal }), 'items');
	if (!Array.isArray(items)) throw bad('activity');
	return items.map(toActivity).filter((i): i is ActivityItem => i !== null);
}

export async function toggleWellDone(id: string, itemId: string): Promise<{ count: number; mine: boolean }> {
	const body = await call('POST', `${seg(id)}/activity/${encodeURIComponent(itemId)}/well-done`);
	const count = field(body, 'count');
	return { count: int(count) && count >= 0 ? count : 0, mine: field(body, 'mine') === true };
}

export async function sendNudge(id: string, input: { targetMemberId: string; phrase: NudgePhrase; date: string }): Promise<void> {
	await call('POST', `${seg(id)}/nudges`, { body: input });
}
